import React, { useMemo, useRef } from "react";
import * as Sentry from "@sentry/react";
import Headline from "../widgets/typography/Headline/Headline";
import { SxProps } from '@mui/system';
import { Card, Box } from '@mui/material';
import { getWidgetTypeInfo, WidgetRegistryItem } from 'components/widgetParts/WidgetRegistry';
import { useDashboard } from "routes/dashboard/Dashboard";
import { DashboardWidgetSpacing } from "components/resources/dashboards/DashboardDocument";
import WidgetExceptionAlert from "components/alerts/WidgetExceptionAlert";
import { Widget } from "system/widget/Widget";

// Required base props for all widgets, can be extended for widget-specific props
export interface WidgetBaseProps {
  widget: Widget;

  widgetRegistryInfo: WidgetRegistryItem,

  isInLayoutMode?: boolean,
  setIsInLayoutMode?: (isInLayoutMode: boolean) => void,

  onDeleteWidget?: (widgetId: string) => void,
  onCopyWidget?: (widget:Widget) => void,

  onUpdateWidget: (widgetId:string, updatedWidgetInfo:any, resetPage?:boolean) => Promise<any>,
  onUpdateWidgetData: (widgetId:string, updatedWidgetData:any, resetPage?:boolean) => Promise<any>,

  widgetRefresh?: () => void,

  widgetWidth?: number,
  widgetHeight?: number,
  contentRef?: any,
  contentPath?: string,
  children?: React.ReactNode;

  shouldUseCachedData: boolean;
}

// Props for this widget container component
export interface WidgetContainerProps {
  widget: Widget;
  isInLayoutMode: boolean,
  setIsInLayoutMode: (isInLayoutMode: boolean) => void,
  onDeleteWidget?: (widgetId: string) => void,
  onCopyWidget?: (widget:Widget) => void,
  onUpdateWidget: (widgetId:string, updatedWidgetInfo:any, resetPage?:boolean) => Promise<any> | undefined,
  onUpdateWidgetData: (widgetId:string, updatedWidgetData:any, resetPage?:boolean) => Promise<any> | undefined,
  children?: React.ReactNode;
}

// Component for displaying a widget's contents in the page grid
export function WidgetContainer(props: WidgetContainerProps): JSX.Element {
  const dashboard = useDashboard();

  // State storing the widget DOM contents for image exports
  // And for measuring the dimensions of the widget
  const contentRef = useRef();

  let widgetContent = <Headline
    widget={props.widget}
    onUpdateWidget={props.onUpdateWidget}
    onUpdateWidgetData={props.onUpdateWidgetData}
  />;

  const hasWidgetData = Object.keys(props.widget || {}).length > 0;

  const widgetRegistryInfo = useMemo(() => {
    if (props?.widget?.name) {
      return getWidgetTypeInfo(props?.widget?.name);
    }
  }, [props?.widget?.name]);

  const widgetRefresh = () => { }

  const widgetTypeInfo = getWidgetTypeInfo(props.widget.name);

  // Widget props to pass into the widget components below
  const widgetProps: WidgetBaseProps = {
    widget: props.widget,
    isInLayoutMode: props.isInLayoutMode,
    setIsInLayoutMode: props.setIsInLayoutMode,
    onDeleteWidget: props.onDeleteWidget,
    onCopyWidget: props.onCopyWidget,
    onUpdateWidget: props.onUpdateWidget,
    onUpdateWidgetData: props.onUpdateWidgetData,
    widgetRefresh: widgetRefresh,
    contentRef: contentRef,
    widgetRegistryInfo: widgetRegistryInfo || {} as WidgetRegistryItem,
    contentPath: widgetTypeInfo.contentPath
  };

  // Render the widget based on its type definition from WidgetRegistry.tsx
  if (widgetTypeInfo) {
    widgetContent = React.createElement(widgetTypeInfo.component, widgetProps);
  }

  // Styling for the widget contents, needs to fill the dashboard grid cell
  const contentSX: SxProps = {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    textAlign: "left",
  }

  if (dashboard?.data?.widgetSpacing === DashboardWidgetSpacing.MINIMAL) {
    contentSX["boxShadow"] = "none";
    contentSX["borderRadius"] = "0px";
  }

  return (
    <Card
      elevation={dashboard?.data?.widgetSpacing === DashboardWidgetSpacing.MINIMAL ? 0 : 1}
      sx={contentSX}
      id={props.widget.uuid}
    >
      <Box sx={contentSX} ref={contentRef}>
        <Sentry.ErrorBoundary fallback={(e: any) => {
          return (
            <WidgetExceptionAlert
              message={e.error.toString()}
              onDeleteWidget={() => {
                props.onDeleteWidget && props.onDeleteWidget(props.widget.id);
              }}
              onResetWidget={() => {
                props.onUpdateWidget && props.onUpdateWidget(props.widget.id, {"request_info": {}, "data": {} });
                document.location.reload();
              }}
          />) }}>
          {hasWidgetData &&
            widgetContent
          }
        </Sentry.ErrorBoundary>
      </Box>
    </Card>
  );
}